import {
  LanguageOutlined,
  OpenInNew,
  PlayArrow,
  ZoomIn,
} from "@mui/icons-material";
import { Box, useTheme } from "@mui/material";
import { memo, useMemo, useState } from "react";
import Lightbox, { Slide } from "yet-another-react-lightbox";
import Video from "yet-another-react-lightbox/plugins/video";
import { AssetType } from "../../../../../types/NendaTypes";

type Props = {
  size?: "small" | "medium" | "large";
  width?: string;
  url: string;
  type: AssetType;
  thumbnail?: string;
};

const ClickableImagePreview = ({
  url,
  type,
  width = "100%",
  thumbnail,
}: Props) => {
  const theme = useTheme();
  const [openImage, setOpenImage] = useState(false);
  const toggleLightBox = () => setOpenImage(!openImage);
  const isVideo = type === AssetType.VIDEO;
  // A URL asset points at a web page, so only a captured screenshot can be
  // shown; falling back to `url` would render a broken image.
  const isUrlAsset = type === AssetType.URL;
  const previewUrl = isVideo ? url : isUrlAsset ? thumbnail : thumbnail || url;
  const slides: Slide[] = useMemo(() => {
    return isVideo
      ? [
          {
            type: "video",
            width: 1280,
            height: 720,
            poster: url,
            sources: [
              {
                src: url,
                type: "video/mp4",
              },
            ],
          },
        ]
      : [
          {
            src: url,
            width: 1280,
            height: 720,
            type: "image",
          },
        ];
  }, [isVideo, url]);
  const render = useMemo(
    () => ({
      buttonPrev: () => null,
      buttonNext: () => null,
    }),
    []
  );
  if (!url) return null;

  // Opening a web page in a lightbox would show a broken image, so a URL asset
  // opens the real page in a new tab instead.
  if (isUrlAsset) {
    return (
      <Box
        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
        sx={{
          width,
          aspectRatio: "16/9",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          // The placeholder needs its own ground: this component renders on a
          // black grid cell AND on a white playlist row, so no single icon
          // colour reads on both. Matches the media library card.
          ...(previewUrl
            ? {}
            : {
                background: theme.palette.transparent.blue,
                borderRadius: "0.2rem",
              }),
        }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            loading="lazy"
            style={{
              width: "100%",
              borderRadius: "0.2rem",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        ) : (
          // Sized relative to the box, not in rem: the same component renders
          // at 5rem wide in a playlist row and full width in the asset grid,
          // and a fixed size is clipped by `overflow: hidden` in the small one.
          <LanguageOutlined
            sx={{
              width: "40%",
              height: "40%",
              color: theme.palette.primary.light,
            }}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transition: "opacity 0.2s ease",
            "&:hover": { opacity: 1, background: "rgba(0,0,0,0.5)" },
          }}
        >
          <OpenInNew
            sx={{
              width: "35%",
              height: "35%",
              color: theme.palette.common.white,
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={toggleLightBox}
      sx={{
        width,
        aspectRatio: "16/9",
        position: "relative",
        overflow: "hidden",
        display: "flex",
      }}
    >
      {url && type !== "video" ? (
        <img
          src={previewUrl}
          loading="lazy"
          style={{
            width: "100%",
            borderRadius: "0.2rem",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />
      ) : (
        <video
          src={previewUrl}
          style={{
            width: "100%",
            borderRadius: "0.2rem",
            objectFit: "contain",
            aspectRatio: "16/9",
          }}
        />
      )}
      {url && (
        <Lightbox
          plugins={type === "video" ? [Video] : []}
          video={{ autoPlay: true, playsInline: true }}
          open={openImage}
          close={toggleLightBox}
          render={render}
          slides={slides}
        />
      )}
      <Box
        onClick={toggleLightBox}
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          transition: "opacity 0.2s ease",
          background: "transparent",
          "&:hover": {
            opacity: 1,
            background: "rgba(0,0,0,0.5)",
          },
        }}
      >
        {type !== AssetType.VIDEO ? (
          <ZoomIn
            sx={{
              fontSize: "3rem",
              color: theme.palette.common.white,
            }}
          />
        ) : (
          <PlayArrow
            sx={{
              fontSize: "3rem",
              color: theme.palette.common.white,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default memo(ClickableImagePreview);
